import translate from '@translate';
import React, { FC, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FormBlock from 'src/components/FormBlock';
import Grid, { GridItem } from 'src/components/Grid';
import TextField from 'src/components/TextField';
import Typography from 'src/components/Typography';
import { setLocationParams } from 'src/resources/location/reducer';
import { RootStoreType } from 'src/types/store';
import BodyTopBlock from 'src/components/BodyTopBlock';
import { checkFormValidity, getEditData } from 'src/utils/formFunctions';
import { updateUserFetch } from 'src/resources/user/reducer';
import Loading from 'src/components/Loading/intex';
import { addMessage } from 'src/resources/alertMessages/reducer';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { logoutFetch } from 'src/resources/login/reducer';
import { sizes } from 'src/themes/default';
import { EyeIcon } from 'src/components/SkladonIcon/SmallIcons';
import { FormErrorsType } from '../../types/global';

export const EDIT_KEYS = ['OLD_PASSWORD', 'PASSWORD', 'PASSWORD_CHECK'];
type EditKeysType = 'OLD_PASSWORD' | 'PASSWORD' | 'PASSWORD_CHECK';
export type ChangePasswordType = { OLD_PASSWORD?: string; PASSWORD?: string; PASSWORD_CHECK?: string };

export interface ChangePasswordContainerPropsType {
    required?: boolean;
}

const ChangePasswordContainer: FC<ChangePasswordContainerPropsType> = (props) => {
    const dispatch = useDispatch();
    const formRef = useRef<HTMLFormElement>(null);

    const loadingEdit = useSelector((state: RootStoreType) => state.user.loadingEdit);

    const [editData, setEditData] = useState<ChangePasswordType>(getEditData({}, EDIT_KEYS));
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});
    const [visiblePasswords, setVisiblePasswords] = useState<string[]>([]);

    const backUrl = '/profil/';
    useEffect(() => {
        dispatch(setLocationParams({ name: translate('changePassword.title'), backUrl }));
    }, []);

    const checkPassowrd = (value: string) => {
        const strongRegex = new RegExp('^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})');
        return strongRegex.test(value);
    };

    const onChange = (key: EditKeysType, value: any, error: boolean, needValidate?: boolean) => {
        const errorValidate = !!needValidate && !checkPassowrd(value);
        const newData = { ...editData };
        const newErrors = { ...editErrors };
        newData[key] = value;
        newErrors[key] = error || errorValidate;
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const handleSave = () => {
        let newFormErrors = checkFormValidity(formRef);

        EDIT_KEYS.forEach((key) => {
            // @ts-ignore
            if (key !== 'OLD_PASSWORD' && !checkPassowrd(String(editData[key] || ''))) {
                newFormErrors[key] = true;
            }
        });

        if (!Object.keys(newFormErrors).length) {
            dispatch(updateUserFetch({ OLD_PASSWORD: editData.OLD_PASSWORD, PASSWORD: editData.PASSWORD }));
        } else {
            dispatch(
                addMessage({
                    type: 'warning',
                    title: translate('messages.formWarningTitle'),
                    text: translate('messages.formWarningText'),
                })
            );
        }

        setEditErrors(newFormErrors);
    };

    const toggleVisiblePassword = (key: string) => {
        if (visiblePasswords.includes(key)) {
            setVisiblePasswords(visiblePasswords.filter((item) => item !== key));
        } else {
            setVisiblePasswords([...visiblePasswords, key]);
        }
    };

    const handlLogout = () => {
        dispatch(logoutFetch(false));
    };

    const notSamePasswords = !!editData.PASSWORD_CHECK && editData.PASSWORD_CHECK !== editData.PASSWORD;

    const formBlock = (
        <form ref={formRef}>
            {loadingEdit && <Loading />}
            <BodyTopBlock
                actions={[
                    {
                        label: translate('changePassword.logout'),
                        onClick: handlLogout,
                        hidden: !props.required,
                        variant: 'outlined',
                        sx: { position: 'absolute', left: sizes.blocks_top_MG },
                    },
                    { label: translate('changePassword.sumbit'), onClick: handleSave },
                ]}
            />

            <FormBlock>
                <Grid>
                    <GridItem cols={12}>
                        <Typography variant="h2" gutterBottom>
                            {translate('changePassword.title')}
                        </Typography>
                        <TextField
                            value={editData.OLD_PASSWORD}
                            error={editErrors.OLD_PASSWORD}
                            onChange={(value, error) => onChange('OLD_PASSWORD', value, error)}
                            name="OLD_PASSWORD"
                            label={translate('changePassword.oldPassword')}
                            fullWidth
                            required
                            type={visiblePasswords.includes('OLD_PASSWORD') ? 'text' : 'password'}
                            endIcon={{
                                onClick: () => {
                                    toggleVisiblePassword('OLD_PASSWORD');
                                },
                                icon: visiblePasswords.includes('OLD_PASSWORD') ? <VisibilityOff /> : <EyeIcon />,
                            }}
                        />
                        <TextField
                            value={editData.PASSWORD}
                            error={editErrors.PASSWORD}
                            onChange={(value, error) => onChange('PASSWORD', value, error, true)}
                            name="PASSWORD"
                            label={translate('changePassword.newPassword')}
                            fullWidth
                            required
                            type={visiblePasswords.includes('PASSWORD') ? 'text' : 'password'}
                            endIcon={{
                                onClick: () => {
                                    toggleVisiblePassword('PASSWORD');
                                },
                                icon: visiblePasswords.includes('PASSWORD') ? <VisibilityOff /> : <EyeIcon />,
                            }}
                            errorHelperText={translate('changePassword.incorretPasswordText')}
                        />
                        <TextField
                            value={editData.PASSWORD_CHECK}
                            error={editErrors.PASSWORD_CHECK || notSamePasswords}
                            onChange={(value, error) => onChange('PASSWORD_CHECK', value, error, true)}
                            name="PASSWORD_CHECK"
                            label={translate('changePassword.comparePassword')}
                            fullWidth
                            required
                            type={visiblePasswords.includes('PASSWORD_CHECK') ? 'text' : 'password'}
                            endIcon={{
                                onClick: () => {
                                    toggleVisiblePassword('PASSWORD_CHECK');
                                },
                                icon: visiblePasswords.includes('PASSWORD_CHECK') ? <VisibilityOff /> : <EyeIcon />,
                            }}
                            errorHelperText={
                                notSamePasswords
                                    ? translate('changePassword.incorretPasswordMatchText')
                                    : translate('changePassword.incorretPasswordText')
                            }
                        />
                    </GridItem>
                </Grid>
            </FormBlock>
        </form>
    );

    if (props.required) {
        return (
            <Dialog maxWidth={'md'} open>
                <DialogContent>{formBlock}</DialogContent>
            </Dialog>
        );
    }

    return formBlock;
};

export default ChangePasswordContainer;
